class PredictiveSearch extends HTMLElement {
	constructor() {
		super();

		this.input = this.querySelector('input[type="search"]');
		this.featuredHeader = this.querySelector('.mm-search-featured-header');
		this.featuredTitle = this.querySelector('[data-search-featured-title]');
		this.productsGrid = this.querySelector('[data-search-products-grid]');
		this.productsSplide = this.querySelector('.mm-search-products-slider');

		this.labelMatching = this.dataset.labelMatching || 'Produits correspondants';
		this.labelSeeAll = this.dataset.labelSeeAll || 'Tout voir';
		this.labelNoResults = this.dataset.labelNoResults || 'Aucun résultat pour votre recherche';
		this.labelDefaultTitle = this.dataset.labelDefaultTitle || '';
		this.searchUrl = this.dataset.searchUrl || '/search';

		// Sauvegarde l'état par défaut pour restauration au close
		if (this.featuredTitle) {
			this.featuredTitle.dataset.defaultTitle = this.featuredTitle.textContent.trim();
		}
		if (this.productsGrid) {
			this.defaultGridHTML = this.productsGrid.innerHTML;
		}

		// Sauvegarde les pills statiques pour restauration au reset
		this.pillsContainer = this.querySelector('.mm-search-pills.mm-desktop');
		this.pillsSplide = this.querySelector('.mm-search-pills-splide');
		if (this.pillsContainer) {
			this.defaultPillsHTML = this.pillsContainer.innerHTML;
		}
		if (this.pillsSplide) {
			this.defaultPillsSplideHTML = this.pillsSplide.querySelector('.splide__list')?.innerHTML || '';
		}

		this.searchRequestId = 0;

		this.input.addEventListener(
			'input',
			this.debounce(() => this.onChange(), 300).bind(this)
		);
	}

	onChange() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			this.reset();
			return;
		}

		this.getSearchResults(searchTerm);
	}

	getSearchResults(searchTerm) {
		const q = encodeURIComponent(searchTerm);
		const requestId = ++this.searchRequestId;

		Promise.all([
			fetch(`/search/suggest?q=${q}&section_id=predictive-search&resources[type]=collection&resources[limit]=6`)
				.then((r) => (r.ok ? r.text() : '')),
			fetch(`/search?q=${q}&type=product&options[prefix]=last&section_id=search-products`)
				.then((r) => (r.ok ? r.text() : '')),
		])
			.then(([suggestText, searchText]) => {
				if (requestId !== this.searchRequestId) return; // Réponse obsolète (requête plus récente en cours)

				const parsedSuggest = new DOMParser().parseFromString(suggestText, 'text/html');
				const parsedSearch = new DOMParser().parseFromString(searchText, 'text/html');

				// Collections depuis suggest
				const collectionItems = parsedSuggest.querySelectorAll('.predictive-search__list-item a[href*="/collections/"]');
				const collections = Array.from(collectionItems).slice(0, 6).map((a) => ({
					title: a.querySelector('p')?.textContent.trim() || a.textContent.trim(),
					url: a.getAttribute('href').split('?')[0],
				}));

				// Produits depuis la recherche fulltext
				const productCards = parsedSearch.querySelectorAll('.mm-product-card');

				this.renderResults(searchTerm, productCards, collections);
			})
			.catch(() => {
				if (requestId === this.searchRequestId) this.reset();
			});
	}

	renderResults(searchTerm, productCards, collections = []) {
		if (!this.productsGrid || !this.featuredTitle) return;

		const count = productCards.length;

		this._removeEmptyState();

		// Met à jour les pills avec les collections trouvées (ou restaure si aucune)
		this._updatePills(collections);

		if (count === 0) {
			this._showEmptyState();
			this.featuredTitle.textContent = this.labelDefaultTitle || this.featuredTitle.dataset.defaultTitle;
			if (this.productsGrid && this.defaultGridHTML !== undefined) {
				this.productsGrid.innerHTML = this.defaultGridHTML;
				window.ProductCardManager?.init();
				this._refreshProductsSplide();
			}
			const seeAllLink = this.featuredHeader?.querySelector('.mm-search-see-all');
			if (seeAllLink) seeAllLink.classList.remove('mm-visible');
			return;
		}

		this._setProductCards(productCards);

		this.featuredTitle.innerHTML = `${this.labelMatching} <span class="mm-search-featured-count">(${count})</span>`;

		if (this.featuredHeader) {
			let seeAllLink = this.featuredHeader.querySelector('.mm-search-see-all');
			if (!seeAllLink) {
				seeAllLink = document.createElement('a');
				seeAllLink.className = 'mm-search-see-all';
				this.featuredHeader.appendChild(seeAllLink);
			}
			seeAllLink.href = `${this.searchUrl}?q=${encodeURIComponent(searchTerm)}&type=product`;
			seeAllLink.textContent = this.labelSeeAll;
			seeAllLink.classList.add('mm-visible');
		}
	}

	_setProductCards(productCards) {
		if (!this.productsGrid) return;

		this.productsGrid.innerHTML = '';
		productCards.forEach((card) => {
			const slide = document.createElement('li');
			slide.className = 'splide__slide';
			slide.appendChild(card.cloneNode(true));
			this.productsGrid.appendChild(slide);
		});
		window.ProductCardManager?.init();
		window.initAddToCartForms?.();
		this._refreshProductsSplide();
	}

	_refreshProductsSplide() {
		if (this.productsSplide?.splide) {
			this.productsSplide.splide.refresh();
		}
	}

	_updatePills(collections) {
		if (!collections.length) {
			this._restorePills();
			return;
		}

		// Desktop pills
		if (this.pillsContainer) {
			this.pillsContainer.innerHTML = collections
				.map((c) => `<a href="${c.url}" class="mm-search-pill mm-cursor-pointer mm-flex mm-align-center mm-justify-center mm-text-small  mm-rounded-full">${c.title}</a>`)
				.join('');
		}

		// Mobile Splide pills
		if (this.pillsSplide) {
			const list = this.pillsSplide.querySelector('.splide__list');
			if (list) {
				list.innerHTML = collections
					.map((c) => `<li class="splide__slide"><a href="${c.url}" class="mm-search-pill mm-cursor-pointer mm-flex mm-align-center mm-justify-center mm-text-small  mm-rounded-full">${c.title}</a></li>`)
					.join('');
			}
			// Rafraîchit Splide si déjà initialisé
			if (this.pillsSplide.splide) {
				this.pillsSplide.splide.refresh();
			}
		}
	}

	_restorePills() {
		if (this.pillsContainer && this.defaultPillsHTML !== undefined) {
			this.pillsContainer.innerHTML = this.defaultPillsHTML;
		}
		if (this.pillsSplide && this.defaultPillsSplideHTML !== undefined) {
			const list = this.pillsSplide.querySelector('.splide__list');
			if (list) list.innerHTML = this.defaultPillsSplideHTML;
			if (this.pillsSplide.splide) this.pillsSplide.splide.refresh();
		}
	}

	_showEmptyState() {
		const banner = document.createElement('div');
		banner.className = 'mm-search-empty-state';
		banner.setAttribute('data-search-empty-state', '');
		banner.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.5" stroke="currentColor"/><path d="M6 5v4M6 4V3" stroke="currentColor" stroke-linecap="round"/></svg><span>${this.labelNoResults}</span>`;

		// Insère avant le .mm-search-featured
		const featured = this.querySelector('[data-search-featured]');
		if (featured) featured.parentNode.insertBefore(banner, featured);
	}

	_removeEmptyState() {
		const existing = this.querySelector('[data-search-empty-state]');
		if (existing) existing.remove();
	}

	reset() {
		this.searchRequestId++; // Invalide toute requête en cours
		this._removeEmptyState();
		this._restorePills();
		if (this.productsGrid && this.defaultGridHTML !== undefined) {
			this.productsGrid.innerHTML = this.defaultGridHTML;
			window.ProductCardManager?.init();
			this._refreshProductsSplide();
		}
		if (this.featuredTitle && this.featuredTitle.dataset.defaultTitle) {
			this.featuredTitle.textContent = this.featuredTitle.dataset.defaultTitle;
		}
		if (this.featuredHeader) {
			const seeAllLink = this.featuredHeader.querySelector('.mm-search-see-all');
			if (seeAllLink) seeAllLink.classList.remove('mm-visible');
		}
	}

	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}
}

customElements.define('predictive-search', PredictiveSearch);
